import React, { useEffect, useRef } from "react";
import { PageLayout } from "../components/page-layout";
import "../styles/components/about-page.css";

export const AboutPage: React.FC = () => {
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const titleElement = titleRef.current;
    if (!titleElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-underline");
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(titleElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <PageLayout fullWidth={true} className="about-layout">
      <div className="about-page-container">
        <div className="about-hero-section">
          <h1 ref={titleRef} className="about-main-title with-underline">Our Story</h1>
          
          <div className="about-split-layout">
            <div className="about-content-left">
              <div className="about-intro-section">
                <p className="about-intro-text">
                  Hi! We're Gymea Edu, a small team of educators and tech nerds on a mission to help teachers spend less time on paperwork and more time doing what they love.
                </p>
              </div>

              <div className="about-section">
                <h3 className="about-section-title">We know what it's like</h3>
                <p className="about-section-text">
                  You're juggling lessons, learning plans, parent emails, and somehow trying to write thoughtful, personalised comments for every student. Teaching is so joyous, but it's also a lot.
                </p>
                <p className="about-section-text">
                  That's why we came together to create Gymea Edu. It can help you generate high-quality, ready-to-edit documents like reports, PLaSPs, and teaching programs in minutes, and free up your energy for what matters most: the students.
                </p>
              </div>

              <div className="about-section">
                <h3 className="about-section-title">What We Believe</h3>
                <ul className="about-beliefs-list">
                  <li>Teachers' time is valuable</li>
                  <li>Admin should take less, not more time</li>
                  <li>Personalisation doesn't have to mean "start from scratch"</li>
                  <li>Tech should help, not add stress</li>
                </ul>
                <p className="about-section-text about-voice-text">
                  We're not here to replace your voice — just to make it faster and easier to say what you already know.
                </p>
              </div>
            </div>

            <div className="about-visual-right">
              <div className="about-visual-card">
                <div className="visual-icon-group">
                  <div className="visual-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                    </svg>
                  </div>
                  <div className="visual-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                      <line x1="12" y1="11" x2="12" y2="17"></line>
                      <line x1="9" y1="14" x2="15" y2="14"></line>
                    </svg>
                  </div>
                  <div className="visual-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 11H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-4m-8 0V9a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m-6 0h6"></path>
                    </svg>
                  </div>
                </div>
                <div className="visual-stats">
                  <div className="stat-item">
                    <div className="stat-number">10K+</div>
                    <div className="stat-label">Documents Generated</div>
                  </div>
                                     <div className="stat-item">
                     <div className="stat-number">100%</div>
                     <div className="stat-label">Built by Teachers</div>
                   </div>
                  <div className="stat-item">
                    <div className="stat-number">100%</div>
                    <div className="stat-label">Locally Owned, Created and Operated</div>
                  </div>
                </div>
              </div>
            </div>
          </div>



          <div className="about-bottom-sections">
            <div className="about-bottom-sections-container">
              <div className="about-sections-grid">
                <div className="about-section">
                  <h3 className="about-section-title">What We Do</h3>
                  <ul className="about-services-list">
                    <li>Help you write clear, personalised student reports</li>
                    <li>Support differentiation with adaptable learning materials</li>
                    <li>Create ready-to-teach programs in seconds</li>
                    <li>Take admin off your plate (and your Sunday afternoon)</li>
                  </ul>
                </div>

                <div className="about-section">
                  <h3 className="about-section-title">Who We Are</h3>
                  <p className="about-section-text">
                    We're educators, tech nerds, and big believers in making teachers' lives easier. We're always listening, always improving, and always up for a chat about how to make Gymea Edu even more useful.
                  </p>
                </div>
              </div>

              <div className="about-closing">
                <p className="about-closing-text">
                  For the love of teaching - thanks for being here.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}; 
